import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { Link } from '../../../../partials/link/Link';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';

interface ScrollableNavProps {
    menuFoldState: AnimationDirectionType;
    animationDuration: number;
    links: NavigationLink[] | null;
    linkColorTypeActive: string;
    linkColorTypeHover: string;
    linkColorTypeNormal: string;
    pagePath: string;
    baseUnit: BaseUnit;
    fontSizesStyles: TranslatedFontSize;
    linkFontSizeLarge: TypographySize;
    linkFontSizeMedium: TypographySize;
    linkFontSizeSmall: TypographySize;
    verticalGapInLargeScreen: number;
    verticalGapInMediumScreen: number;
    verticalGapInSmallScreen: number;
    secondRowBackgroundColor: string;
    secondRowHeightInMediumScreen: number;
    secondRowHeightInSmallScreen: number;
}

const getStyles = (secondRowBackgroundColor: string, baseUnit: number, secondRowHeightInMediumScreen: number, secondRowHeightInSmallScreen: number) => {
  return StyleSheet.create({
    scrollableNav: {
      backgroundColor: secondRowBackgroundColor,
      overflowX: 'auto',
      alignItems: 'center',
      zIndex: 50,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${secondRowHeightInMediumScreen * baseUnit}px`,
        display: 'flex',
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${secondRowHeightInSmallScreen * baseUnit}px`,
        display: 'flex',
      },
    },
    scrollableUl: {
      display: 'flex',
      padding: 0,
    },
  });
};

const getLinksStyle = (
  activeStateColor: string,
  hoverStateColor: string,
  idleColor: string,
  isActive: boolean,
  baseUnit: BaseUnit,
  verticalGapInLargeScreen: number,
  verticalGapInMediumScreen: number,
  verticalGapInSmallScreen: number,
) => StyleSheet.create({
  li: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    [MEDIA_BREAKPOINTS.large]: {
      marginLeft: `${baseUnit * verticalGapInLargeScreen}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginLeft: `${baseUnit * verticalGapInMediumScreen}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit * verticalGapInSmallScreen}px`,
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.medium]: {
        marginRight: `${baseUnit * verticalGapInMediumScreen}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginRight: `${baseUnit * verticalGapInSmallScreen}px`,
      },
    },
  },
  link: {
    color: isActive ? activeStateColor : idleColor,
    textDecoration: 'none',
    ':hover': {
      color: hoverStateColor,
    },
  },
});

const getLinks = (
  links: NavigationLink[],
  activeStateColor: string,
  hoverStateColor: string,
  idleColor: string,
  pagePath: string,
  baseUnit: BaseUnit,
  fontSizesStyles: TranslatedFontSize,
  linkFontSizeLarge: TypographySize,
  linkFontSizeMedium: TypographySize,
  linkFontSizeSmall: TypographySize,
  verticalGapInLargeScreen: number,
  verticalGapInMediumScreen: number,
  verticalGapInSmallScreen: number,
) => {
  return links.map((navigationLink, index) => {
    const isActive = pagePath === navigationLink.link.href;
    const style = getLinksStyle(activeStateColor, hoverStateColor, idleColor, isActive, baseUnit, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen);
    const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: linkFontSizeSmall, fontSizeMedium: linkFontSizeMedium, fontSizeLarge: linkFontSizeLarge });
    return (
      <li key={index} className={css(style.li)}>
        <Link href={navigationLink.link.href} className={css(style.link, fontStyle)}>
          {navigationLink.link.text}
        </Link>
      </li>
    );
  });
};

const createHeaderSecondRowStyleWithAnimation = (animationDuration: number) => {
  return StyleSheet.create({
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(0)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(0)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
  });
};

const getHeaderSecondRowStyleWithAnimation = (menuFoldState: AnimationDirectionType, animationDuration: number) => {
  const style = createHeaderSecondRowStyleWithAnimation(animationDuration);
  return menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
};

export const ScrollableNav: React.FunctionComponent<ScrollableNavProps> = props => {
  const {
    menuFoldState,
    animationDuration,
    links,
    linkColorTypeActive,
    linkColorTypeHover,
    linkColorTypeNormal,
    pagePath,
    baseUnit,
    fontSizesStyles,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    secondRowBackgroundColor,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen } = props;
  const styles = getStyles(secondRowBackgroundColor, baseUnit, secondRowHeightInMediumScreen, secondRowHeightInSmallScreen);
  return (
    <nav className={css(getHeaderSecondRowStyleWithAnimation(menuFoldState, animationDuration), styles.scrollableNav)}>
      <ul className={css(styles.scrollableUl)}>
        {getLinks(links || [], linkColorTypeActive, linkColorTypeHover, linkColorTypeNormal, pagePath, baseUnit, fontSizesStyles, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen)}
      </ul>
    </nav>
  );
};
